/**
 * SignOutWarningModal - Data Loss Warning for Free Users
 *
 * CRITICAL UX: Free users (no cloud sync) lose ALL local data on sign-out.
 * This modal warns them and offers export before sign-out.
 *
 * Only shown for free users (!isPro). Pro users sign out immediately.
 */

import { useState, useCallback } from 'react';
import { Modal } from './shared/Modal';
import { Button } from './shared';
import { useLanguage } from '@/i18n';
import './SignOutWarningModal.css';

export type SignOutScope = 'local' | 'everywhere';

interface SignOutWarningModalProps {
  isOpen: boolean;
  scope: SignOutScope;
  onClose: () => void;
  onExport: () => Promise<void>;
  onConfirm: () => Promise<void>;
  isSigningOut: boolean;
  isExporting: boolean;
}

export function SignOutWarningModal({
  isOpen,
  scope,
  onClose,
  onExport,
  onConfirm,
  isSigningOut,
  isExporting,
}: SignOutWarningModalProps) {
  const { t } = useLanguage();
  const [exported, setExported] = useState(false);

  // Reset state when modal closes
  const handleClose = useCallback(() => {
    if (!isSigningOut) {
      setExported(false);
      onClose();
    }
  }, [isSigningOut, onClose]);

  // Handle export - download backup
  const handleExport = useCallback(async () => {
    await onExport();
    setExported(true);
  }, [onExport]);

  // Handle confirm sign out
  const handleConfirm = useCallback(async () => {
    await onConfirm();
  }, [onConfirm]);

  const isEverywhere = scope === 'everywhere';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('signout.title')}
      size="sm"
      preventClose={isSigningOut}
    >
      <div className="signout-warning-modal">
        {/* Warning Icon */}
        <div className="warning-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>

        {/* Warning Text */}
        <div className="warning-text">
          <h3>{t('signout.warn_title')}</h3>
          <p>{t('signout.warn_body')}</p>
          <ul>
            <li>{t('signout.item_transactions')}</li>
            <li>{t('signout.item_accounts')}</li>
            <li>{t('signout.item_budgets')}</li>
          </ul>
          <p className="no-sync-warning">
            {t('signout.no_sync_warning')}
          </p>
          {isEverywhere && (
            <p className="everywhere-note">
              {t('signout.everywhere_note')}
            </p>
          )}
        </div>

        {/* Export Recommendation */}
        <div className={`export-recommendation ${exported ? 'exported' : ''}`}>
          {exported ? (
            <div className="export-success">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{t('signout.backup_done')}</span>
            </div>
          ) : (
            <>
              <span className="recommendation-label">{t('signout.rec_label')}</span>
              <span className="recommendation-text">{t('signout.rec_text')}</span>
            </>
          )}
        </div>

        {/* Signing Out Progress */}
        {isSigningOut && (
          <div className="signout-progress">
            <div className="progress-spinner" />
            <p>{t('settings.account.signing_out')}</p>
          </div>
        )}

        {/* Actions */}
        {!isSigningOut && (
          <div className="modal-actions">
            <Button
              variant="secondary"
              onClick={handleExport}
              disabled={isExporting}
              className="export-button"
            >
              {isExporting ? t('settings.export.exporting') : exported ? t('signout.export_again') : t('signout.export')}
            </Button>
            <div className="action-row">
              <Button
                variant="ghost"
                onClick={handleClose}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirm}
              >
                {t('signout.sign_out_anyway')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
